import { Edit as EditIcon } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { GridLogicOperator, type GridFilterModel } from '@mui/x-data-grid';
import {
  ItemInfrastructureDto,
  UpdateItemDto,
  entityType,
  ProjectDesignerRoleTags,
} from '@nohara-dxdev/mt-common';
import { permission } from '@nohara-dxdev/oidc-core';
import { OidcProtectedComponent, useOidc, useScope, useScopeIds } from '@nohara-dxdev/react-oidc';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/confirm-dialog';
import { TasksList } from '@/features/tasks';
import { ItemEntity } from '@/types/api';

import { useItemLockToggle } from '../api';
import { useItemTemplateSchema } from '../api/get-item-template-schema';
import { useUpdateItem } from '../api/update-item';
import { useInfrastructureRequirementsCheck } from '../hooks/use-infrastructure-requirements-check';
import { ItemFilterValuesProvider, useItemFilterValues } from '../providers';

import { BulkEditDialog } from './bulk-edit';
import { ItemFormData } from './forms';
import { ItemForm } from './item-form';

const updateItemPermissions = [permission.item.update];
const lockPermissions = [permission.item.lock];
const unlockPermissions = [permission.item.unlock];
export interface EditItemDialogProps {
  item: ItemEntity;
  open: boolean;
  onClose: () => void;
}

/**
 * Controlled editable view of an item's attributes. Renders the item's
 * template-driven form together with the lock/unlock, bulk-edit and tasks
 * affordances. Kept separate from the icon-button trigger so it can be opened
 * from other surfaces (e.g. clicking a node on the Load List graph — MT1-1416),
 * mirroring the read-only ViewItemDialog.
 */
export const EditItemDialog = ({ item, open, onClose }: EditItemDialogProps) => {
  const { t } = useTranslation();
  const subject = useScopeIds();
  const { ability } = useOidc();
  const { scope } = useScope();
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // Define filter values for item template selection
  // This can be customized based on the context of item creation
  const projectRoleTagsFilter = useMemo(() => {
    const allowedProjectRoleTags = Object.values(ProjectDesignerRoleTags).filter((tag) =>
      ability?.has(
        { action: 'update', subject: 'item', scope },
        { itemTemplate: { projectRoleTags: [tag] } },
      ),
    );
    if (allowedProjectRoleTags.length === Object.values(ProjectDesignerRoleTags).length) {
      return [];
    }
    return allowedProjectRoleTags;
  }, [ability, scope]);

  const {
    data: itemTemplate,
    isLoading: isLoadingTemplate,
    error: templateError,
  } = useItemTemplateSchema(item.itemTemplateId, {
    queryConfig: { enabled: open && !!item.itemTemplateId },
    version: item.itemTemplate?.itemTemplateVersion,
  });

  const { mutateAsync: updateItem, isPending } = useUpdateItem(item.id);

  const isLoading = isLoadingTemplate;
  const hasError = templateError;

  const { mutateAsync: toggleLock, isPending: isTogglingLock } = useItemLockToggle(item.id);

  const isDisabled = isPending || isTogglingLock;

  const isLocked = item.locked;

  // Check infrastructure requirements
  const requirementsCheck = useInfrastructureRequirementsCheck({
    infrastructureRequirements: itemTemplate?.infrastructureRequirements,
    infrastructures: (item.infrastructures ?? []) as ItemInfrastructureDto[],
    currentItemId: item.id,
  });

  const [showLockConfirmation, setShowLockConfirmation] = useState(false);

  const handleToggleLock = async () => {
    // If locking and requirements are not satisfied, show confirmation dialog
    if (!isLocked && !requirementsCheck.areAllRequirementsSatisfied) {
      setShowLockConfirmation(true);
      return;
    }

    await toggleLock(!item.locked);
  };

  const handleConfirmLock = async () => {
    setShowLockConfirmation(false);
    await toggleLock(!item.locked);
  };

  const handleCancelLock = () => {
    setShowLockConfirmation(false);
  };

  const handleUpdate = async (data: UpdateItemDto) => {
    await updateItem(data);
    handleClose();
  };

  const formData = useMemo((): ItemFormData => {
    return {
      id: item.id,
      itemNumber: item.itemNumber,
      name: item.name,
      attributes: item.attributes,
      location: item.location,
      adjacentLocation: item.adjacentLocation,
      constructionTypeId: item.constructionTypeId,
      systemIds: item.systemIds ?? [],
      status: item.status,
      quotationTypeId: item.quotationTypeId,
      locationTypeId: item.locationTypeId,
      infrastructures: (item.infrastructures ?? []) as ItemInfrastructureDto[],
      circuits: item.circuits ?? [],
      infrastructureRelationshipConfigs: item.infrastructureRelationshipConfigs ?? [],
    };
  }, [item]);

  // Start each open — and each new item while the dialog stays mounted — with a
  // clean dirty flag, so neither reopening nor switching the shown item (e.g.
  // clicking a different node on the Load List graph) resurfaces a stale
  // "discard changes?" prompt from the previous item.
  useEffect(() => {
    if (open) {
      setIsFormDirty(false);
    }
  }, [open, item.id]);

  const handleClose = () => {
    onClose();
  };

  const handleDialogClose = (_event: unknown, reason: string) => {
    if ((reason === 'backdropClick' || reason === 'escapeKeyDown') && isFormDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    handleClose();
  };

  const handleCancelDiscard = () => {
    setDiscardConfirmOpen(false);
  };

  const handleConfirmDiscard = () => {
    setDiscardConfirmOpen(false);
    setIsFormDirty(false);
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('items.edit.title')}</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : hasError ? (
            <Box textAlign="center" py={4}>
              <Typography color="error">
                {t('items.edit.error', 'Failed to load item data')}
              </Typography>
            </Box>
          ) : itemTemplate ? (
            <OidcProtectedComponent
              requireAll={updateItemPermissions}
              subject={{ ...subject }}
              context={{
                itemTemplate: { projectRoleTags: item.itemTemplate?.projectRoleTags },
              }}
              fallbackElement={
                <Typography color="error">
                  {t('common.unauthorized', 'You do not have permission to edit this item')}
                </Typography>
              }
            >
              <ItemFormWithMergedFilters
                itemTemplate={itemTemplate}
                isDisabled={isDisabled || item.locked}
                onSubmit={handleUpdate}
                onDirtyChange={setIsFormDirty}
                formData={formData}
                projectRoleTagsFilter={projectRoleTagsFilter}
              />
              <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <TasksList filters={{ entityId: item.id, entityType: entityType.item }} />
              </Box>
            </OidcProtectedComponent>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">
                {t('items.edit.noData', 'No item data found')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', gap: 1, ml: 1 }}>
            <OidcProtectedComponent
              requireAll={isLocked ? unlockPermissions : lockPermissions}
              subject={{ ...subject }}
              context={{
                itemTemplate: { projectRoleTags: item.itemTemplate?.projectRoleTags },
              }}
            >
              <Button
                type="button"
                variant="contained"
                color={isLocked ? 'success' : 'error'}
                disabled={isDisabled}
                onClick={() => void handleToggleLock()}
              >
                {isLocked ? t('common.unlock') : t('common.lock')}
              </Button>
            </OidcProtectedComponent>
            <OidcProtectedComponent
              requireAll={updateItemPermissions}
              subject={{ ...subject }}
              context={{
                itemTemplate: { projectRoleTags: item.itemTemplate?.projectRoleTags },
              }}
            >
              <Button
                type="button"
                variant="contained"
                color="success"
                disabled={isDisabled || isLocked}
                onClick={() => {
                  setBulkEditOpen(true);
                }}
              >
                {t('items.bulkEdit.button')}
              </Button>
            </OidcProtectedComponent>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: 1, mr: 1 }}>
            <Button onClick={handleClose} disabled={isDisabled}>
              {t('common.cancel')}
            </Button>
            <OidcProtectedComponent
              requireAll={updateItemPermissions}
              subject={{ ...subject }}
              context={{
                itemTemplate: { projectRoleTags: item.itemTemplate?.projectRoleTags },
              }}
            >
              <Button
                type="submit"
                form="edit-item-form"
                variant="contained"
                color="primary"
                disabled={isDisabled || item.locked}
              >
                {t('common.submit')}
              </Button>
            </OidcProtectedComponent>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Dialog */}
      {itemTemplate && (
        <BulkEditDialog
          open={bulkEditOpen}
          onClose={() => {
            setBulkEditOpen(false);
          }}
          mainItem={item}
          itemTemplate={itemTemplate}
        />
      )}

      {/* Missing Relationship Confirmation Dialog */}
      <Dialog open={showLockConfirmation} onClose={handleCancelLock} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('items.lock.missingRelationship.title', 'Missing Relationship')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {(() => {
              // Get the first missing requirement to show in the message
              const firstMissing = Array.from(requirementsCheck.missingRequirements.values())[0];
              const infrastructureTypeName = firstMissing
                ? t(
                    `infrastructure.${firstMissing.infrastructureType}`,
                    firstMissing.infrastructureType,
                  )
                : t('infrastructure.electricity', 'Electricity');

              return t(
                'items.lock.missingRelationship.message',
                'The required parent relationship for the {{infrastructureType}} Infrastructure is missing. Do you still want to lock this item?',
                { infrastructureType: infrastructureTypeName },
              );
            })()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLock}>{t('common.cancel')}</Button>
          <Button onClick={() => void handleConfirmLock()} variant="contained" color="error">
            {t('common.lock')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={discardConfirmOpen}
        onClose={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
        title={t('items.edit.discardTitle', 'Discard unsaved changes?')}
        message={t(
          'items.edit.discardMessage',
          'You have unsaved changes. Do you want to discard them and close?',
        )}
        confirmText={t('items.edit.discardConfirm', 'Discard')}
        cancelText={t('items.edit.discardCancel', 'Keep editing')}
      />
    </>
  );
};

export interface EditItemButtonProps {
  item: ItemEntity;
}

export const EditItemButton = ({ item }: EditItemButtonProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip title={t('items.edit.tooltip', 'Edit item')}>
        <IconButton size="small" onClick={handleOpen} color="primary">
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <EditItemDialog item={item} open={open} onClose={handleClose} />
    </>
  );
};

// Component to merge filterModel with parent context
const ItemFormWithMergedFilters = ({
  itemTemplate,
  isDisabled,
  onSubmit,
  onDirtyChange,
  formData,
  projectRoleTagsFilter,
}: {
  itemTemplate: NonNullable<ReturnType<typeof useItemTemplateSchema>['data']>;
  isDisabled: boolean;
  onSubmit: (data: UpdateItemDto) => Promise<void>;
  onDirtyChange: (isDirty: boolean) => void;
  formData: ItemFormData | undefined;
  projectRoleTagsFilter: ProjectDesignerRoleTags[];
}) => {
  const parentFilterValues = useItemFilterValues();

  // Manually merge parent filterModel with projectRoleTags filter
  const mergedFilterModel = useMemo<GridFilterModel[] | undefined>(() => {
    const parentFilterModels = parentFilterValues.filterModel ?? [];
    const projectRoleTagsItems =
      projectRoleTagsFilter.length > 0
        ? [
            {
              field: 'projectRoleTags',
              operator: 'isAnyOf' as const,
              value: projectRoleTagsFilter as string[],
            },
          ]
        : [];

    // If no filters to merge, return undefined
    if (parentFilterModels.length === 0 && projectRoleTagsItems.length === 0) {
      return undefined;
    }

    // Start with parent filterModels
    const mergedFilterModels: GridFilterModel[] = [...parentFilterModels];

    // Add projectRoleTags filter as a new filterModel in the array
    if (projectRoleTagsItems.length > 0) {
      mergedFilterModels.push({
        items: projectRoleTagsItems,
        logicOperator: GridLogicOperator.And,
      });
    }

    return mergedFilterModels.length > 0 ? mergedFilterModels : undefined;
  }, [parentFilterValues, projectRoleTagsFilter]);

  return (
    <ItemFilterValuesProvider
      filterValues={{
        filterModel: mergedFilterModel,
      }}
    >
      <ItemForm
        template={itemTemplate}
        disabled={isDisabled}
        onSubmit={onSubmit}
        onDirtyChange={onDirtyChange}
        initialData={formData}
        formId="edit-item-form"
      />
    </ItemFilterValuesProvider>
  );
};
